import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  SxProps,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import {BaseEntityForm} from "../../../core/model/BaseForm";
import {BaseEntity} from "../../../core/model/BaseEntity";
import {ReactNode, useEffect} from "react";
import UploadButton from "../../upload";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {translate} from "@util";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import ColorPicker from "./ColorPicker";
import Password from "./Password";
import dayjs, {Dayjs} from "dayjs";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import {UserSelector} from "@components/UserSelector";
import {AssetSelector} from "@components/asset-selector";
import {DateTimePicker} from "@mui/x-date-pickers";
import {MetadataInput} from "@components/MetadataInput";
import {InputArray, TagsSelector} from "@components";

export type FormLabel = {
  entity: string;
  save: string;
  reset: string;
  edit: string;
  new: string;
};

type Props<T extends BaseEntity> = {
  label?: Partial<FormLabel>;
  form: BaseEntityForm;
  data: Partial<T>;
  selectValues?: {[key: string]: {id: string | number; name: string}[]};
  isLoading?: boolean;
  isFetching?: boolean;
  CustomButton?: ReactNode;
  sx?: SxProps<Theme>;
  onFieldChange?: (field: string, value: any) => void;
  onSave: (data: T) => void;
  onReset?: () => void;
};

export default function CrudForm<T extends BaseEntity>(props: Props<T>) {
  const {
    form,
    data,
    label,
    isLoading,
    isFetching,
    selectValues,
    CustomButton,
    sx,
    onFieldChange,
    onSave,
    onReset,
  } = props;
  const theme = useTheme();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: {errors},
  } = useForm({
    defaultValues: data as any,
    resolver: form.validation && yupResolver(form.validation),
  });

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const handleChange = (field: string, value: any) => {
    setValue(field, value);
    trigger(field);
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const renderFields = () => {
    const fields = form.columns.map(col => {
      const fieldValue = getValues(col.field);

      if ((col.fieldType === "id" && !fieldValue) || col.hideForm) {
        return null;
      }

      if (data?.id && col.hideEdit) {
        return null;
      }

      let errorMsg = errors[col.field]?.message?.toString();
      errorMsg = errorMsg ? translate(errorMsg) : errorMsg;
      const hasError = errorMsg !== undefined;

      if (col.formInput) {
        const CustomInput = col.formInput;
        return (
          <CustomInput
            key={"edit_form_" + col.field}
            // valori correnti del form, non i dati iniziali
            value={getValues()}
            onChange={v => handleChange(col.field, v)}
          />
        );
      }

      return (
        <FormControl fullWidth margin="normal" key={"edit_form_" + col.field}>
          <Controller
            name={col.field}
            control={control}
            render={() => {
              switch (col.fieldType) {
                case "image":
                case "file":
                  return (
                    <>
                      <UploadButton
                        name={col.field}
                        label={translate(col.headerName ?? "undefined")}
                        fileType={col.fieldType}
                        errorMsg={errorMsg}
                        onChange={fileId => handleChange(col.field, fileId)}
                        fileId={
                          fieldValue?.trim() === "" ? undefined : fieldValue
                        }
                        disabled={isLoading || isFetching}
                      />
                    </>
                  );
                case "select":
                  return (
                    <TextField
                      disabled={isLoading || isFetching}
                      variant="standard"
                      size="small"
                      select
                      label={translate(col.headerName ?? "undefined")}
                      error={hasError}
                      helperText={errorMsg}
                      value={fieldValue ?? ""}
                      {...register(col.field)}
                      onChange={e => handleChange(col.field, e.target.value)}>
                      {selectValues?.[col.field].map(e => (
                        <MenuItem key={col.field + "_" + e.id} value={e.id}>
                          {translate(e.name ?? "")}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                case "date":
                  return (
                    <DatePicker
                      value={fieldValue ? dayjs(fieldValue) : null}
                      onChange={(v: any) => {
                        const value = v as Dayjs | undefined;
                        const ts =
                          value && value.isValid()
                            ? value.unix() * 1000
                            : undefined;
                        handleChange(col.field, ts);
                      }}
                      slotProps={{
                        openPickerButton: {size: "small"},
                        openPickerIcon: {fontSize: "inherit"},
                        textField: {
                          variant: "standard",
                          size: "small",
                          label: translate(col.headerName ?? "undefined"),
                          type: "text",
                          error: hasError,
                          helperText: errorMsg,
                          disabled: isLoading || isFetching,
                        },
                      }}
                    />
                  );
                case "datetime":
                  return (
                    <DateTimePicker
                      value={fieldValue ? dayjs(fieldValue) : null}
                      onChange={(v: any) => {
                        const value = v as Dayjs | undefined;
                        const ts =
                          value && value.isValid()
                            ? value.unix() * 1000
                            : undefined;
                        handleChange(col.field, ts);
                      }}
                      slotProps={{
                        openPickerButton: {size: "small"},
                        openPickerIcon: {fontSize: "inherit"},
                        textField: {
                          variant: "standard",
                          size: "small",
                          label: translate(col.headerName ?? "undefined"),
                          type: "text",
                          error: hasError,
                          helperText: errorMsg,
                          disabled: isLoading || isFetching,
                        },
                      }}
                    />
                  );
                case "color":
                  return (
                    <ColorPicker
                      label={translate(col.headerName ?? "undefined")}
                      value={getValues(col.field)}
                      colors={col.colors}
                      disabled={isLoading || isFetching}
                      errorMsg={errorMsg}
                      onChange={color => handleChange(col.field, color)}
                    />
                  );
                case "password":
                  return (
                    <Password
                      name={col.field}
                      autoComplete="off"
                      variant="standard"
                      size="small"
                      disabled={isLoading || isFetching}
                      label={translate(col.headerName ?? "undefined")}
                      type={col.fieldType}
                      error={hasError}
                      helperText={errorMsg}
                      onChange={e => handleChange(col.field, e.target.value)}
                    />
                  );
                case "boolean":
                  return (
                    <FormControlLabel
                      label={translate(col.headerName ?? "")}
                      sx={{
                        color: theme.palette.text.primary,
                      }}
                      control={
                        <Switch
                          checked={fieldValue ?? false}
                          color="secondary"
                          onClick={() => handleChange(col.field, !fieldValue)}
                        />
                      }
                    />
                  );
                case "user":
                  return (
                    <UserSelector
                      label={col.headerName}
                      roles={col.userRoles}
                      value={getValues(col.field) ?? ""}
                      onChange={id => handleChange(col.field, id)}
                    />
                  );
                case "asset":
                  return (
                    <AssetSelector
                      label={col.headerName}
                      value={getValues(col.field) ? [getValues(col.field)] : []}
                      onChange={asset =>
                        handleChange(col.field, asset ? asset[0] : undefined)
                      }
                    />
                  );
                case "metadata":
                  return (
                    <MetadataInput
                      label={col.headerName}
                      value={getValues(col.field) ?? {}}
                      onChange={id => handleChange(col.field, id)}
                    />
                  );
                case "array":
                  return (
                    <InputArray
                      label={col.headerName}
                      value={getValues(col.field) ?? ""}
                      onChange={e => handleChange(col.field, e.target.value)}
                    />
                  );
                case "tags":
                  return (
                    <TagsSelector
                      error={errorMsg}
                      label={col.headerName}
                      value={getValues(col.field) ?? []}
                      onChange={tags => handleChange(col.field, tags)}
                    />
                  );
                default:
                  return (
                    <TextField
                      variant={
                        col.fieldType === "paragraph" ? "outlined" : "standard"
                      }
                      size="small"
                      multiline={col.fieldType === "paragraph"}
                      minRows={col.minRows ?? 2}
                      maxRows={col.maxRows}
                      autoComplete="off"
                      disabled={
                        col.fieldType === "id" || isLoading || isFetching
                      }
                      label={translate(col.headerName ?? "undefined")}
                      type={col.fieldType}
                      error={hasError}
                      helperText={errorMsg}
                      value={getValues(col.field) ?? ""}
                      {...register(col.field)}
                      onChange={e => handleChange(col.field, e.target.value)}
                    />
                  );
              }
            }}
          />
        </FormControl>
      );
    });

    return <>{fields}</>;
  };

  const onSubmit = (data: any) => {
    Object.keys(data).forEach(k => {
      if (data[k] === "-") {
        data[k] = null;
      }
    });
    onSave(data);
  };

  const handleReset = () => {
    reset(data);
    if (onReset) {
      onReset();
    }
  };

  return (
    <Stack
      sx={sx}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      direction={"column"}>
      <Stack
        direction="column"
        className="crud-form-fields-container"
        sx={{opacity: isFetching || isLoading ? 0.8 : 1}}>
        {renderFields()}
      </Stack>
      <Stack
        spacing={2}
        className="crud-form-button-container"
        direction="row"
        marginTop={4}
        justifyContent={"end"}>
        {CustomButton}
        <Button
          variant="text"
          onClick={handleReset}
          disabled={isLoading || isFetching}>
          {label?.reset ? translate(label.reset) : "Reset"}
        </Button>
        <Button
          variant="contained"
          disabled={isFetching}
          loading={isLoading}
          type="submit"
          startIcon={<SaveIcon />}>
          {label?.save ? translate(label.save) : "Save"}
        </Button>
      </Stack>
    </Stack>
  );
}
